import * as React from "react";
import * as Select from "@radix-ui/react-select";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { SelectItem } from "@/components/ui/select";
import { TRAFFIC_LIGHT_OPTIONS, TrafficLightRating } from "@/modules/snapshots/businessReports.types";
import { EditToolbar } from "./editToolbar";

interface Props {
  editedContent: string;
  editedRating: TrafficLightRating | null;
  isLoading: boolean;
  showRating: boolean;
  onContentChange: (content: string) => void;
  onRatingChange: (rating: TrafficLightRating | null) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const EditMode: React.FC<Props> = ({
  editedContent,
  editedRating,
  isLoading,
  showRating,
  onContentChange,
  onRatingChange,
  onSave,
  onCancel,
}) => (
  <div className="space-y-2.5">
    <EditToolbar isLoading={isLoading} onSave={onSave} onCancel={onCancel} />

    {showRating && (
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Rating</span>
        <Select.Root
          value={editedRating ?? undefined}
          onValueChange={value => onRatingChange(value as TrafficLightRating)}
        >
          <Select.Trigger
            className="inline-flex items-center justify-between gap-2 rounded border border-gray-300 bg-white px-3 py-1.5 text-xs outline-none hover:bg-gray-50 focus:border-transparent focus:ring-2 focus:ring-violet-500 data-[placeholder]:text-gray-400"
            aria-label="Select rating"
          >
            <Select.Value placeholder="Select rating" />
            <Select.Icon>
              <ChevronDown className="h-3 w-3" />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content
              position="popper"
              className="z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
            >
              <Select.ScrollUpButton className="flex h-6 cursor-default items-center justify-center bg-white text-violet-700">
                <ChevronUp className="h-3 w-3" />
              </Select.ScrollUpButton>

              <Select.Viewport className="p-1">
                {TRAFFIC_LIGHT_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </Select.Viewport>

              <Select.ScrollDownButton className="flex h-6 cursor-default items-center justify-center bg-white text-violet-700">
                <ChevronDown className="h-3 w-3" />
              </Select.ScrollDownButton>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>
    )}

    <Textarea
      value={editedContent}
      onChange={e => onContentChange(e.target.value)}
      className="min-h-[250px] border border-coral/20 focus:border-coral focus:ring-coral"
      placeholder="Enter content here..."
    />
  </div>
);
